import { useNavigate, useParams } from 'react-router-dom';
import {
  useLeadsActionCreateMutation,
  useLeadsGetSingleLeadQuery,
  useLeadStageChangeMutation,
  useLeadStatusChangeMutation,
  useLeadsUpdateMutation,
} from '../../../reduxToolkit/reducers/usersSlice';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Col,
  Card,
  CardBody,
  Container,
  Row,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Button,
  CardHeader,
  CardFooter,
  Label,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';
import { useTranslation } from 'react-i18next';
import { baseLang } from '../../../lib/utils';

import Loader from '../../../layout/Loader';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
import { Image } from '../../../coreComponents/Image';

import {
  LeadInput,
  LeadsCancelReasons,
  LeadStageStatus,
  LeadStatus,
  TaskType,
} from '../../../types/Leads';

import Swal from 'sweetalert2';
import CommonCardHeader from '../../../coreComponents/CommonCardHeader';

import LeadActionsWrapper from './LeadActionsWrapper';
import TimePickerWithLimitedTime from '../../formsAndTable/forms/formWidgets/datePicker/timePicker/TimePickerWithLimitedTime';
import Error404 from '../../pages/others/errorPage/Error404';
import { routes } from '../../../routers/routes';
import CommentFileModal from '../../commetFileModal';
import { getCancelLeadReasons } from '../../../services/api';
import { toISODate } from '../../../lib/dashboardUtils';
import { formatWithOffset } from '../../../lib/timeUtils';
import { co } from '@fullcalendar/core/internal-common';

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const LeadsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const {
    data: leadData,
    isError,
    isSuccess,
    isLoading,
  } = useLeadsGetSingleLeadQuery({ id: id as string }, { skip: !id });
  const pageLang = baseLang(i18n.language);
  const [breadcrumbs, setBreadcrumbs] = useState<{ name: string; link: string }[]>([]);
  const [showCreateAction, setShowCreateAction] = useState(false);
  const [showUpdateAction, setShowUpdateAction] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [leadFlowInputs, setLeadFlowInputs] = useState<LeadInput[] | null>(null);
  const [openLeadDetails, setLeadDetails] = useState<string[]>([]);

  // Newly selected files (not yet uploaded)
  const [newFiles, setNewFiles] = useState<File[]>([]);
  // Mixed list of previews: existing from server + new ones
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false);
  const [comment, setComment] = useState('');
  const [closeReason, setCloseReason] = useState<{
    id: number;
    statusName: string;
    reason: string;
    colorCode: string;
    type: string | null;
    isSystem: boolean;
  } | null>(null);
  const [isCloseCommentRequired, setIsCloseCommentRequired] = useState(false);

  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [leadStatus, setLeadStatus] = useState<LeadStatus>(LeadStatus.NEW);

  const [leadUpdateHandler] = useLeadsUpdateMutation();
  const [leadChangeStatusHandler] = useLeadStatusChangeMutation();
  const [leadCreateAction] = useLeadsActionCreateMutation();
  const [leadStageChangeHandler] = useLeadStageChangeMutation();

  const [newAction, setNewAction] = useState<{
    id?: number;
    taskType: TaskType | null;
    title: string;
    deadline: Date | null;
  }>({
    id: Number(id),
    taskType: null,
    title: '',
    deadline: null,
  });
  const navigate = useNavigate();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  useEffect(() => {
    setBreadcrumbs([
      {
        name: t('leads.sidebarLeadsTitle'),
        link: '/leadslist',
      },
    ]);
  }, [pageLang]);
  const showFullImageHandler = (url: string) => {
    setFullImageUrl(url);
    setShowFullImage(true);
  };
  const toggleLeadDetails = useCallback((id: string) => {
    setLeadDetails((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const updateCommmentHandler = async (data: FormData) => {
    try {
      const res = await leadUpdateHandler(data).unwrap();
      await sleep(500);
      if (!res.success && !res?.code) {
        Swal.fire({
          icon: 'error',
          title: t('leads.notifications.errorTitle'),
          text: t('leads.notifications.errorText'),
        });
      }
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: t('leads.notifications.successTitle'),
          text: t('leads.notifications.successText'),
        });
      }
    } catch (error) {
      console.log('error', error);
      Swal.fire({
        icon: 'error',
        title: t('leads.notifications.errorTitle'),
        text: t('leads.notifications.errorText'),
      });
    }
  };
  const handleOpenCreateActionModal = (id: number) => {
    setNewAction({
      id: Number(id),
      taskType: null,
      title: '',
      deadline: null,
    });

    setShowCreateAction(true);
  };

  useEffect(() => {
    if (leadData?.status) {
      const status = leadData.status;
      setLeadStatus(status);
      (async () => {
        const data = await getCancelLeadReasons();
        if (Array.isArray(data)) {
          // store raw data and translate later in a language-aware effect
          setCancelReasonsRaw(data);
        }
        const preselected = (data as any[]).find((option) => option.reason === 'other');
        setCloseReasonRawId(preselected?.id ?? null);
        setIsCloseCommentRequired(true);
      })();
    }

    if (leadData?.flows && leadData.flows.length > 0) {
      const flowInputs = leadData.flows.map((f, i) => {
        return {
          id: f.id,
          data: f.dataRows,
          showFullImageHandler,
          position: f.position,
          updated: f.updatedAt,
          stage: f.stage,
          stageStatus: f.stageStatus,
          handleOpenCreateActionModal: handleOpenCreateActionModal,
          setNewAction: (id: number) => setNewAction((prev) => ({ ...prev, id })),
        };
      });
      const sortedInputs = flowInputs.sort((a, b) => a.position - b.position);
      setLeadFlowInputs(sortedInputs);
    }
  }, [leadData]);
  const abortNewAction = () => {
    setNewAction({
      id: Number(id),
      taskType: null,
      title: '',
      deadline: null,
    });
    setShowCreateAction(false);
  };

  const handleCreateNewAction = async () => {
    if (!newAction?.deadline || !newAction.title?.trim() || !newAction.taskType) {
      return;
    }
    setShowCreateAction(false);
    try {
      const res = await leadCreateAction(newAction).unwrap();
      await sleep(500);
      Swal.fire({
        icon: res.success ? 'success' : 'error',
        title: res.success
          ? t('leads.notifications.successTitle')
          : t('leads.notifications.errorTitle'),
        text: res.success
          ? t('leads.notifications.successText')
          : t('leads.notifications.errorText'),
      });
    } catch (error) {
      console.log('error', error);
      Swal.fire({
        icon: 'error',
        title: t('leads.notifications.errorTitle'),
        text: t('leads.notifications.errorText'),
      });
    }
  };

  const handleChangeStageStatus = async (payload: { flowId: number; status: LeadStageStatus }) => {
    try {
      const res = await leadStageChangeHandler(payload).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };
  const taskTypesSelects = [
    {
      id: 1,
      value: TaskType.CALL,
      title: t('leads.addActionForm.selectCall'),
    },
    {
      id: 2,
      value: TaskType.EMAIL,
      title: t('leads.addActionForm.selectEmail'),
    },
    {
      id: 3,
      value: TaskType.OTHER,
      title: t('leads.addActionForm.selectOther'),
    },
  ];

  const handleLeadStatusDecline = async (payload: { leadId: number; status: LeadStatus }) => {
    setShowDeclineConfirmation(true);
  };

  const handleSubmitStatusChange = async () => {
    const payload = {
      leadId: leadData!.id,
      status: LeadStatus.DECLINED,
      cancelReason: closeReason,
      cancelComment: comment,
    };
    console.log('Submitting status change with payload:', payload);
    try {
      const res = await leadChangeStatusHandler(payload).unwrap();
      handleCommentFileModalClose();
      await sleep(500);
      if (!res.success && !res?.code) {
        Swal.fire({
          icon: 'error',
          title: t('leads.notifications.errorTitle'),
          text: t('leads.notifications.errorText'),
        });
      }
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: t('leads.notifications.successTitle'),
          text: t('leads.notifications.successText'),
        });
      }
    } catch (error) {
      console.log('error', error);
      Swal.fire({
        icon: 'error',
        title: t('leads.notifications.errorTitle'),
        text: t('leads.notifications.errorText'),
      });
    }
  };

  const [dropDownOptions, setDropDownOptions] = useState<any[]>([]);
  const [cancelReasonsRaw, setCancelReasonsRaw] = useState<any[] | null>(null);
  const [closeReasonRawId, setCloseReasonRawId] = useState<number | null>(null);

  useEffect(() => {
    if (!cancelReasonsRaw) return;
    const translated = cancelReasonsRaw.map((item) => ({
      id: item.id,
      colorCode: item.colorCode,
      statusName: t(`global.closeReason.${item.reason}`),
      reason: item.reason,
      type: item.type,
      isSystem: true,
    }));
    setDropDownOptions(translated);
    if (closeReasonRawId != null) {
      const sel = translated.find((opt) => opt.id === closeReasonRawId) || null;
      setCloseReason(sel);
    }
  }, [cancelReasonsRaw, pageLang, closeReasonRawId, t]);

  const reasonChangeHandler = (value: any) => {
    const selectedOption = dropDownOptions.find((option) => option.id === value.id);
    if (
      selectedOption &&
      (selectedOption.reason === LeadsCancelReasons.OTHER ||
        selectedOption.reason === LeadsCancelReasons.REFUSED_FOREVER ||
        selectedOption.reason === LeadsCancelReasons.REFUSED_TEMPORARILY)
    ) {
      setIsCloseCommentRequired(true);
    } else {
      setIsCloseCommentRequired(false);
    }
    setCloseReason(selectedOption);
  };
  const handleCommentFileModalClose = () => {
    setShowDeclineConfirmation(false);
    setComment('');
    const initialReason =
      dropDownOptions.find((option) => option.reason === LeadsCancelReasons.OTHER) || null;
    setCloseReason({
      id: initialReason?.id,
      colorCode: initialReason?.colorCode,
      statusName: initialReason ? t(`global.closeReason.${initialReason.reason}`) : '',
      reason: initialReason?.reason || '',
      type: initialReason?.type || null,
      isSystem: true,
    });
    setIsCloseCommentRequired(true);
  };

  const leadStatusChangeHandler = async (payload: { leadId: number; status: LeadStatus }) => {
    try {
      Swal.fire({
        title: t('leads.notifications.checkingStatusTitle'),
        text: t('leads.notifications.checkingStatusText'),
        icon: 'info',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      await sleep(1000);
      const res = await leadChangeStatusHandler(payload).unwrap();
      console.log('Lead status change response:', res);
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: t('leads.notifications.successChangeStatusTitle'),
          text: t('leads.notifications.successChangeStatusText'),
        });
      } else if (res.message === 'no-activity') {
        Swal.fire({
          icon: 'warning',
          title: t('leads.notifications.noActivityTitle'),
          text: t('leads.notifications.noActivityText'),
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('leads.notifications.errorTitle'),
          text: t('leads.notifications.errorText'),
        });
      }
    } catch (error) {
      console.log('error', error);
      Swal.fire({
        icon: 'error',
        title: t('leads.notifications.errorTitle'),
        text: t('leads.notifications.errorText'),
      });
    }
  };

  if (isLoading)
    return (
      <>
        <Loader />
      </>
    );
  if (isError)
    return (
      <Error404 goto={routes.Leads.LeadsList} buttonLabel={t('global.backButtonLabel.leadsList')} />
    );
  if (isSuccess)
    return (
      <>
        <Breadcrumbs
          mainTitle={t('leads.leadDetails')}
          // parent={t('courses.generalTitle')}
          parentsArray={breadcrumbs}
        />
        <Container fluid>
          <Row>
            <Card>
              {/* <CommonCardHeader title={lessonTranslation.lessonTitle} tag tagClass="pb-4" /> */}
              <CardBody>
                <Col sm="12" className="d-flex">
                  <Col sm="6">
                    <Row>
                      <Col sm="3">
                        <p>{t('leads.table.name')}:</p>
                      </Col>
                      <Col sm="9">
                        <p>{leadData.fullName}</p>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="3">
                        <p>Email:</p>
                      </Col>
                      <Col sm="9">
                        <p>{leadData.email}</p>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="3">
                        <p>{t('leads.table.phone')}:</p>
                      </Col>
                      <Col sm="9">
                        <p>{leadData.phone}</p>
                      </Col>
                    </Row>
                  </Col>
                  <Col sm="6">
                    <Row className="d-flex justify-content-between position-relative">
                      <Row className="ps-4">
                        <Col sm="12" className="d-flex gap-1 ps-0">
                          {' '}
                          <p className="m-0">{t('leads.status.title')}:</p>
                          <p className="m-0">{t(`leads.status.${leadStatus}`)}</p>
                        </Col>
                        {leadData.userId && (
                          <div className="position-absolute ribbon-wrapper-right top-0 ">
                            <div className="ribbon ribbon-right ribbon-info ribbon-bookmark ribbon-space-bottom top-0 end-0 me-3">
                              {t(`leads.status.convertedToUser`)}
                            </div>
                          </div>
                        )}
                        {leadData.cancel_reason && leadData.closed_at && (
                          <Col xs="auto" className="m-0 p-0">
                            <div className="d-flex flex-column">
                              <p className="m-0">
                                {t('leads.cancelReason.title')}
                                {t(`global.closeReason.${leadData.cancel_reason}`)}
                              </p>
                              <p className="m-0" id={`lead-${leadData.id}-comment`}>
                                {t('leads.cancelReason.comment')}
                                {leadData?.cancel_comment && leadData.cancel_comment.length > 30
                                  ? `${leadData.cancel_comment.substring(0, 30)}...`
                                  : leadData?.cancel_comment}
                              </p>
                              {leadData?.cancel_comment && leadData.cancel_comment.length > 30 && (
                                <UncontrolledTooltip
                                  target={`lead-${leadData.id}-comment`}
                                  placement="bottom-start"
                                  container="body"
                                  delay={{ show: 50, hide: 50 }}
                                  style={{
                                    minWidth: '200px',
                                    backgroundColor: 'var(--balance-profie-bg)',
                                    textAlign: 'left',
                                  }}
                                >
                                  {leadData?.cancel_comment}
                                </UncontrolledTooltip>
                              )}
                              <p className="m-0">
                                {t('leads.cancelReason.date', {
                                  date: formatWithOffset(leadData.closed_at, timezone, undefined),
                                })}
                              </p>
                            </div>
                          </Col>
                        )}
                      </Row>

                      <Col sm="12" className="mt-2 ">
                        <Row className="g-2 align-items-center w-100">
                          {/* <Col xs="auto" className="m-0">
                            <Button
                              color="danger"
                              onClick={() => {
                                const body = {
                                  leadId: leadData.id,
                                  status: LeadStatus.DECLINED,
                                };
                                handleLeadStatusDecline(body);
                              }}
                              disabled={leadData.status !== LeadStatus.NEW}
                            >
                              {t('leads.status.declined')}
                            </Button>
                          </Col>
                          <Col xs="auto" className="m-0">
                            <Button
                              color="primary"
                              onClick={() => {
                                const body = {
                                  leadId: leadData.id,
                                  status: LeadStatus.CONVERTED,
                                };
                                leadChangeStatusHandler(body);
                              }}
                              disabled={leadData.status !== LeadStatus.NEW}
                            >
                              {t('leads.status.converted')}
                            </Button>
                          </Col> */}
                          {leadData.userId && (
                            <Col xs="auto" className="mt-0 p-0 ms-auto me-0 justify-self-end">
                              <Button
                                color="primary"
                                outline
                                // className=" ps-1 hover:text-black"
                                className="mt-3"
                                onClick={() =>
                                  navigate(`/dashboard/stat-details/${leadData.userId}`)
                                }
                              >
                                {t('stats.userStats')}
                              </Button>
                            </Col>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Col>

                <Row>
                  <Col sm="6" className="pt-2">
                    <Accordion
                      open={openLeadDetails}
                      toggle={toggleLeadDetails}
                      className="dark-accordion"
                      flush={true}
                    >
                      <AccordionItem key={1}>
                        <AccordionHeader
                          className="bg-light rounded border-secondary"
                          targetId={String(leadData.id)}
                        >
                          <span className="text-muted">{t('leads.metadata.title')}</span>
                        </AccordionHeader>
                        <AccordionBody accordionId={String(leadData.id)}>
                          <Col sm="12">
                            <Card>
                              <CommonCardHeader
                                title={t('leads.metadata.header', { name: leadData.fullName })}
                                tag
                              />

                              <CardBody>
                                <Row className="mb-2">
                                  <Col sm="12">
                                    {leadData.sourceMeta &&
                                    Object.keys(leadData.sourceMeta).length > 0 ? (
                                      Object.entries(leadData.sourceMeta).map(([key, value]) => (
                                        <Row key={key} className="mb-1">
                                          <Col sm="3">
                                            <strong>{key}:</strong>
                                          </Col>
                                          <Col sm="9">{String(value)}</Col>
                                        </Row>
                                      ))
                                    ) : (
                                      <span>{t('leads.metadata.noMeta')}</span>
                                    )}
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </AccordionBody>
                      </AccordionItem>
                    </Accordion>
                  </Col>
                  <Col sm="6" className="ps-0 pt-2 d-flex align-items-center gap-2">
                    <Col xs="auto" className="m-0">
                      <Button
                        color="danger"
                        onClick={() => {
                          const body = {
                            leadId: leadData.id,
                            status: LeadStatus.DECLINED,
                          };
                          handleLeadStatusDecline(body);
                        }}
                        disabled={leadData.status !== LeadStatus.NEW}
                      >
                        {t('leads.status.declined')}
                      </Button>
                    </Col>
                    <Col xs="auto" className="m-0">
                      <Button
                        color="primary"
                        onClick={() => {
                          const body = {
                            leadId: leadData.id,
                            status: LeadStatus.CONVERTED,
                          };
                          // leadChangeStatusHandler(body);
                          leadStatusChangeHandler(body);
                        }}
                        disabled={leadData.status !== LeadStatus.NEW}
                      >
                        {t('leads.status.converted')}
                      </Button>
                    </Col>
                  </Col>
                  {/* {leadData.cancel_reason && leadData.closed_at && (
                    <Col sm="6" className="pt-2">
                      <div className="d-flex flex-column">
                        <span>
                          {t('leads.cancelReason.title')}
                          {t(`global.closeReason.${leadData.cancel_reason}`)}
                        </span>
                        <span>
                          {t('leads.cancelReason.comment')}
                          {leadData.cancel_comment}
                        </span>
                        <span>
                          {t('leads.cancelReason.date', {
                            date: formatWithOffset(leadData.closed_at, timezone, undefined),
                          })}
                        </span>
                      </div>
                    </Col>
                  )} */}
                </Row>
                {leadFlowInputs?.map((input) => (
                  <LeadActionsWrapper
                    {...input}
                    leadData={leadData}
                    leadStatus={leadStatus}
                    onAddComment={async (data: FormData) => {
                      updateCommmentHandler(data);
                      // send to backend: comment + files (FormData)
                      // await api.leads.addFlowComment(flowId, comment, files)
                    }}
                    onStageStatusChange={(data: { flowId: number; status: LeadStageStatus }) => {
                      handleChangeStageStatus(data);
                    }}
                    onRemoveExistingFile={async ({ flowId, url }) => {
                      console.log('LeadActionsWrapper Removing file:', { flowId, url });
                    }}
                    onRowCommentClick={(row, flowId) => {
                      // open modal or focus the composer
                      // openCommentModal(flowId, row)
                    }}
                  />
                ))}
              </CardBody>
            </Card>
          </Row>
        </Container>
        <SimpleModal
          isOpen={showFullImage}
          close={() => setShowFullImage(false)}
          size="xl"
          centered
        >
          {fullImageUrl && <Image src={fullImageUrl} className="object-fit-cover w-100" />}
        </SimpleModal>
        <SimpleModal isOpen={showCreateAction} close={() => setShowCreateAction(false)}>
          <>
            <Card>
              <CardHeader>
                <CommonCardHeader
                  title={t('leads.addActionForm.mainTitle')}
                  tagClass="text-unclamp"
                />
              </CardHeader>
              <CardBody>
                <Label for="actionTitle">{t('leads.addActionForm.label')}</Label>
                <Input
                  type="text"
                  name="actionTitle"
                  value={newAction?.title}
                  onChange={(e) =>
                    setNewAction({
                      ...newAction,
                      title: e.target.value,
                    })
                  }
                ></Input>
                <Label className="mt-3 w-full d-flex flex-column">
                  {t('leads.addActionForm.labelTaskType')}{' '}
                </Label>
                <Input
                  type="select"
                  required
                  name="taskTypeStatus"
                  value={newAction.taskType ?? ''}
                  onChange={(e) => {
                    const selected = e.target.value as TaskType;

                    setNewAction({
                      ...newAction,
                      taskType: selected,
                    });
                  }}
                >
                  <option value="" disabled hidden>
                    {t('leads.addActionForm.selectPlaceholder')}
                  </option>
                  {taskTypesSelects.map((tt) => (
                    <option key={tt.id} value={tt.value}>
                      {tt.title}
                    </option>
                  ))}
                </Input>
                <Label className="mt-3">{t('leads.addActionForm.setDeadline')}</Label>
                <TimePickerWithLimitedTime
                  setDateTime={(date) => {
                    setNewAction((prev) => {
                      return {
                        ...prev,
                        deadline: date,
                      };
                    });
                  }}
                  start={0}
                  disabledPeriods={[]}
                  initialSelection={null}
                  preferExactSelection={true}
                  workingHours={{ start: '07:00', end: '22:00' }}
                />
              </CardBody>
              <CardFooter>
                <Row className="g-2">
                  <Col xs="auto">
                    <Button color="primary" onClick={handleCreateNewAction}>
                      {t('global.ok')}
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button onClick={abortNewAction}>{t('global.cancel')}</Button>
                  </Col>
                </Row>
              </CardFooter>
            </Card>
          </>
        </SimpleModal>
        <CommentFileModal
          isOpen={showDeclineConfirmation}
          onClose={handleCommentFileModalClose}
          labels={{
            title: t('leads.commentForm.label'),
            placeholder: t('leads.commentForm.placeholder'),
            ok: t('global.ok'),
            saving: t('global.saving'),
            cancel: t('global.cancel'),
            remove: t('common.remove'),
            selectStatus: t('global.selector.selectStatus'),
          }}
          commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
          commentRequired={isCloseCommentRequired}
          comment={comment}
          onCommentChange={setComment}
          onSubmit={handleSubmitStatusChange}
          dropDown={true}
          hasFileInput={false}
          dropDownContent={dropDownOptions}
          dropDownPreSelected={closeReason}
          dropDownSelectHandler={reasonChangeHandler}
          filesRequired={false}
          isClearable={false}
        />
      </>
    );
  return null;
};

export default LeadsDetails;
